import { useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ApolloClient, InMemoryCache, ApolloProvider, split, HttpLink } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { AuthContext } from './context/AuthContext';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import MySchedule from './pages/MySchedule';
import ShiftDetails from './pages/ShiftDetails';
import LeaveRequest from './pages/LeaveRequest';
import AvailabilityPage from './pages/AvailabilityPage';
import ProfilePage from './pages/ProfilePage';

const GQL_HTTP_URL = import.meta.env.VITE_GQL_HTTP || 'http://localhost:4000/graphql';
const GQL_WS_URL = import.meta.env.VITE_GQL_WS || 'ws://localhost:4000/graphql';

function App() {
  const authContext = useContext(AuthContext);
  const isAuthenticated = authContext?.isAuthenticated ?? false;
  const token = authContext?.token ?? null;

  const httpLink = new HttpLink({
    uri: GQL_HTTP_URL,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: GQL_WS_URL,
      connectionParams: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  const client = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
  });

  return (
    <ApolloProvider client={client}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/schedule"
            element={isAuthenticated ? <MySchedule /> : <Navigate to="/login" />}
          />
          <Route
            path="/shift/:id"
            element={isAuthenticated ? <ShiftDetails /> : <Navigate to="/login" />}
          />
          <Route
            path="/leave"
            element={isAuthenticated ? <LeaveRequest /> : <Navigate to="/login" />}
          />
          <Route
            path="/availability"
            element={isAuthenticated ? <AvailabilityPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/profile"
            element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" />}
          />
        </Routes>
      </BrowserRouter>
    </ApolloProvider>
  );
}

export default App;
